/**
 * ============================================================================
 * ReferralForm / ReferralFormNew — Shared Controller (client)
 * ============================================================================
 * Flags: programs prop + sticky CTA + strict fullName + refer-more reset.
 * Local 6-box OTP via checkstatus/otp + verify; submit referralform.
 * RHF + explicit resolver; paint-first busy; no Yup. No JSX.
 * INP: submit-mode + per-field trigger after paint (same as other lead forms).
 * ============================================================================
 */
"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";
import {
  useForm,
  useWatch,
  type Control,
  type Path,
  type PathValue,
} from "react-hook-form";
import { useLocalLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { setLeadFormInputValue } from "@/helpers/leadFormDegreeProgramFields";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";
import { usePathname } from "next/navigation";
import { fetchIpInfoData, getIpJSONData } from "@/context/ip.storage.utils";
import {
  errorAccordingToFieldName,
  regexForInputs,
} from "@/components/organisms/faq-hub/HomeNewFaq/HomeConnectModalFormFAQ/utils";
import { useIpStackDetails } from "@/helpers/useIpStackDetails";
import useOnScreen from "@/helpers/Onscreen";
import {
  applyOtpDigit,
  applyOtpPaste,
  createEmptyOtp,
  focusOtpInput,
  formatOtpTimer,
  OTP_LENGTH,
  OTP_TTL_SECONDS,
} from "../../../shared/referralOtp";
import {
  sendReferralCheckStatusOtp,
  submitReferralLead,
  trackReferralCounsellor,
  verifyReferralFormOtp,
} from "../service/referralForm.service";
import {
  createReferralFormResolver,
  EMPTY_PROGRAM,
  EMPTY_REFERRER_DATA,
  NATIONAL_CODE,
  validateCounsellorUsername,
  validateReferralEmail,
  validateReferralFullName,
  validateReferralMobile,
  validateVerificationEmailOrMobile,
  type ReferralFormValues,
  type ReferralProgramOption,
  type ReferralReferrerData,
} from "../model/referralForm.types";
import { programs as defaultPrograms } from "../utils";

const OTP_ID_PREFIX = "otp-input-";

const OTP_GATE_FIELDS: Path<ReferralFormValues>[] = [
  "fullName",
  "email",
  "countryCode",
  "mobile",
  "program",
  "counsellorUsername",
  "verificationEmailOrMobile",
];

export interface UseReferralFormControllerOptions {
  /** CMS / API programs (ReferralFormNew). Falls back to static utils list. */
  programs?: ReferralProgramOption[];
  /** Toggle sticky "enroll-now-referral-cta" via useOnScreen (New only). */
  enableStickyCta?: boolean;
  /** Stricter fullName rules used by ReferralFormNew. */
  strictFullName?: boolean;
  /** Reset form on "Refer More!" instead of navigating (New only). */
  enableReferMoreReset?: boolean;
}

export interface ReferralFormViewModel {
  step: number;
  // react-hook-form bindings — views bind fields via <Controller control={control} />.
  control: Control<ReferralFormValues>;
  programs: ReferralProgramOption[];
  errors: Partial<Record<string, string | undefined>>;
  showOtpBox: boolean;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  isValidationDisabled: boolean;
  isValidationUsed: boolean;
  timer: number;
  resendEnabled: boolean;
  timerLabel: string;
  stickyCtaRef: RefObject<HTMLDivElement | null> | null;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleProgramChange: (index: number) => void;
  handleCountryCodeChange: (fieldName: string, value: string) => void;
  updateCountryCode: (value: string) => void;
  fetchOTP: () => void;
  resendOTP: (e?: React.MouseEvent) => void;
  handleOtpChange: (index: number, value: string) => void;
  handleOtpKeyDown: (index: number, e: KeyboardEvent<HTMLInputElement>) => void;
  handlePaste: (e: ClipboardEvent<HTMLInputElement>, index: number) => void;
  otpRef: (el: HTMLInputElement | null, index: number) => void;
  handleFormSubmit: (e: FormEvent) => void;
  handleReferMore: () => void;
}

export function useReferralFormController(
  options: UseReferralFormControllerOptions = {},
): ReferralFormViewModel {
  const {
    programs: programsProp,
    enableStickyCta = false,
    strictFullName = false,
    enableReferMoreReset = false,
  } = options;

  const programs: ReferralProgramOption[] =
    programsProp && programsProp.length > 0 ? programsProp : defaultPrograms;

  const path = usePathname();
  const isAfricaPage = path == "/africa";
  const { ipStackDetails, countryCode } = useIpStackDetails(isAfricaPage);

  const stickyRef = useRef<HTMLDivElement | null>(null);
  const onScreen = useOnScreen(stickyRef);

  const [step, setStep] = useState(1);
  const [newData, setNewData] =
    useState<ReferralReferrerData>(EMPTY_REFERRER_DATA);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showOtpBox, setShowOtpBox] = useState(false);
  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [isValidationUsed, setIsValidationUsed] = useState(false);
  // Survives RHF resolver re-validation after checkstatus/otp NotSent.
  const [verificationServerError, setVerificationServerError] = useState<
    string | undefined
  >();
  const isFiredOnce = useRef(false);
  const otpInFlight = useRef(false);

  const resolver = useMemo(
    () => createReferralFormResolver({ strictFullName }),
    [strictFullName],
  );

  const defaultValues: ReferralFormValues = {
    fullName: "",
    email: "",
    countryCode: NATIONAL_CODE,
    mobile: "",
    program: { ...EMPTY_PROGRAM },
    counsellorUsername: "",
    verificationEmailOrMobile: "",
    otp: createEmptyOtp(),
  };

  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
    formState: { errors: rhfErrors, isSubmitting: rhfSubmitting },
  } = useForm<ReferralFormValues>({
    defaultValues,
    // Keep submit-mode for untouched fields. Live errors still come from
    // setLeadFormInputValue → trigger(field) after paint (INP-safe).
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver,
  });

  useLocalLeadFormValidationReset({
    clearErrors,
    isFormHidden: showOtpBox,
  });

  // Narrow useWatch subscriptions for side-effect effects only. Field values
  // in the view come from per-field <Controller> render props.
  const watchedFullName = useWatch({ control, name: "fullName" });
  const watchedEmail = useWatch({ control, name: "email" });
  const watchedMobile = useWatch({ control, name: "mobile" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedProgram = useWatch({ control, name: "program" });
  const watchedCounsellorUsername = useWatch({
    control,
    name: "counsellorUsername",
  });
  const watchedVerification = useWatch({
    control,
    name: "verificationEmailOrMobile",
  });
  const watchedOtp = useWatch({ control, name: "otp" });

  const isSubmitting = isSubmitUIBusy || rhfSubmitting;

  const errors = useMemo(
    () => ({
      fullName: rhfErrors.fullName?.message as string | undefined,
      email: rhfErrors.email?.message as string | undefined,
      countryCode: rhfErrors.countryCode?.message as string | undefined,
      mobile: rhfErrors.mobile?.message as string | undefined,
      program: rhfErrors.program?.message as string | undefined,
      verificationEmailOrMobile:
        (rhfErrors.verificationEmailOrMobile?.message as string | undefined) ||
        verificationServerError,
      otp: rhfErrors.otp?.message as string | undefined,
      counsellorUsername: rhfErrors.counsellorUsername?.message as
        | string
        | undefined,
    }),
    [
      rhfErrors.fullName,
      rhfErrors.email,
      rhfErrors.countryCode,
      rhfErrors.mobile,
      rhfErrors.program,
      rhfErrors.verificationEmailOrMobile,
      rhfErrors.otp,
      rhfErrors.counsellorUsername,
      verificationServerError
    ],
  );

  useEffect(() => {
    if (!enableStickyCta) return;
    const enrollNowRefProgram = document.getElementById(
      "enroll-now-referral-cta",
    );
    if (enrollNowRefProgram) {
      enrollNowRefProgram.style.display = onScreen ? "none" : "flex";
    }
  }, [enableStickyCta, onScreen]);

  useEffect(() => {
    setValue("countryCode", countryCode, { shouldValidate: false });
  }, [ipStackDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cheap field checks — not formState.isValid (that re-runs the full resolver
  // each keystroke / INP). Button enablement stays independent of error UI.
  const isFormReadyForOtp = useMemo(
    () =>
      !validateReferralFullName(watchedFullName ?? "", strictFullName) &&
      !validateReferralEmail(watchedEmail ?? "") &&
      !validateReferralMobile(
        watchedMobile ?? "",
        watchedCountryCode ?? NATIONAL_CODE,
      ) &&
      (watchedProgram?.ProgramID ?? 0) !== 0 &&
      !validateCounsellorUsername(watchedCounsellorUsername ?? "") &&
      !validateVerificationEmailOrMobile(watchedVerification ?? ""),
    [
      strictFullName,
      watchedCounsellorUsername,
      watchedCountryCode,
      watchedEmail,
      watchedFullName,
      watchedMobile,
      watchedProgram,
      watchedVerification,
    ],
  );

  const isOtpComplete =
    (watchedOtp ?? []).length === OTP_LENGTH &&
    (watchedOtp ?? []).every((digit) => Boolean(digit));
  const isValidationDisabled =
    isValidationUsed ||
    isSubmitting ||
    !isFormReadyForOtp ||
    Boolean(verificationServerError);
  const isSubmitDisabled =
    isSubmitting ||
    !isFormReadyForOtp ||
    !showOtpBox ||
    !isOtpComplete ||
    Boolean(verificationServerError);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
    setResendEnabled(true);
    return undefined;
  }, [timer]);

  const fireIpOnce = useCallback(() => {
    if (!isFiredOnce.current) {
      fetchIpInfoData();
      isFiredOnce.current = true;
    }
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      if (name === "verificationEmailOrMobile") {
        setVerificationServerError(undefined);
      }
      setLeadFormInputValue(
        setValue,
        trigger,
        name as Path<ReferralFormValues>,
        value as PathValue<ReferralFormValues, Path<ReferralFormValues>>,
      );
      fireIpOnce();
    },
    [fireIpOnce, setValue, trigger],
  );

  const handleProgramChange = useCallback(
    (selectedValue: number) => {
      const selectedProgram = programs.find((p) => p.index === selectedValue);
      setValue(
        "program",
        selectedValue !== 0 && selectedProgram
          ? {
              ProgramID: selectedProgram.ProgramID,
              ProgramName: selectedProgram.ProgramName,
              Specialization: selectedProgram.Specialization,
              index: selectedProgram.index,
            }
          : { ...EMPTY_PROGRAM },
        { shouldDirty: true, shouldTouch: true, shouldValidate: false },
      );
      scheduleAfterPaint(() => {
        void trigger("program");
      });
    },
    [programs, setValue, trigger],
  );

  const handleCountryCodeChange = useCallback(
    (fieldName: string, value: string) => {
      if (!regexForInputs[fieldName]?.test(value)) {
        setError("countryCode", {
          type: "validate",
          message: errorAccordingToFieldName[fieldName],
        });
        return;
      }
      setValue("countryCode", value, {
        shouldDirty: true,
        shouldValidate: false,
      });
      scheduleAfterPaint(() => {
        void trigger("countryCode");
        void trigger("mobile");
      });
    },
    [setError, setValue, trigger],
  );

  const updateCountryCode = useCallback(
    (value: string) => {
      setValue("countryCode", value, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue("mobile", "", { shouldDirty: true, shouldValidate: false });
      clearErrors("mobile");
    },
    [clearErrors, setValue],
  );

  const fetchOTP = useCallback(() => {
    if (isSubmitting || isValidationUsed || otpInFlight.current) return;
    if (verificationServerError) return;
    if (showOtpBox && !resendEnabled) return;

    otpInFlight.current = true;
    scheduleAfterPaint(() => {
      void (async () => {
        try {
          const valid = await trigger(OTP_GATE_FIELDS);
          if (!valid) return;

          setIsValidationUsed(true);

          const emailOrPhone = getValues("verificationEmailOrMobile");
          const result = await sendReferralCheckStatusOtp(emailOrPhone);
          const res = result.response ?? result.data?.response;
          const status = res?.success as any;

          setNewData(res || EMPTY_REFERRER_DATA);

          if (result.ok && status) {
            setVerificationServerError(undefined);
            setShowOtpBox(true);
            setTimer(OTP_TTL_SECONDS);
            setResendEnabled(false);
            clearErrors("otp");
            return;
          }

          const message =
            !status || !result?.response?.success
              ? "Your email/phone is not registered."
              : "Something went wrong. Please try again later.";
          setVerificationServerError(message);
          setError("verificationEmailOrMobile", {
            type: "server",
            message,
          });
        } finally {
          otpInFlight.current = false;
          setIsSubmitUIBusy(false);
          setIsValidationUsed(false);
        }
      })();
    });
  }, [
    clearErrors,
    getValues,
    isSubmitting,
    isValidationUsed,
    resendEnabled,
    setError,
    showOtpBox,
    trigger,
    verificationServerError,
  ]);

  const resendOTP = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      setValue("otp", createEmptyOtp());
      clearErrors("otp");
      void fetchOTP();
    },
    [clearErrors, fetchOTP, setValue],
  );

  const handleOtpChange = useCallback(
    (index: number, value: string) => {
      const current = getValues("otp");
      const applied = applyOtpDigit(current, index, value);
      if (!applied) return;
      setValue("otp", applied.otp, { shouldDirty: true });
      clearErrors("otp");
      if (applied.nextFocusIndex !== null) {
        otpRefs.current[applied.nextFocusIndex]?.focus();
      }
    },
    [clearErrors, getValues, setValue],
  );

  const otpRef = useCallback((el: HTMLInputElement | null, index: number) => {
    otpRefs.current[index] = el;
  }, []);

  const handleOtpKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      const isArrowLeft = e.key === "ArrowLeft";
      const isArrowRight = e.key === "ArrowRight";
      const isBackspace = e.key === "Backspace";
      const otp = getValues("otp");

      if (isArrowLeft || isArrowRight) {
        const moveToIndex = isArrowLeft ? index - 1 : index + 1;
        if (moveToIndex >= 0 && moveToIndex < otp.length) {
          e?.preventDefault?.();
          focusOtpInput(OTP_ID_PREFIX, moveToIndex);
        }
      } else if (isBackspace && !otp[index]) {
        const prevIndex = index - 1;
        if (prevIndex >= 0) {
          e?.preventDefault?.();
          const updated = [...otp];
          updated[prevIndex] = "";
          setValue("otp", updated);
          focusOtpInput(OTP_ID_PREFIX, prevIndex);
        }
      }
    },
    [getValues, setValue],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>, index: number) => {
      e?.preventDefault?.();
      const pasteData = e.clipboardData.getData("Text");
      const current = getValues("otp");
      const applied = applyOtpPaste(current, index, pasteData);
      if (!applied) return;
      setValue("otp", applied.otp, { shouldDirty: true });
      const nextIndex = applied.lastFilledIndex + 1;
      if (nextIndex < current.length) {
        focusOtpInput(OTP_ID_PREFIX, nextIndex);
      } else {
        focusOtpInput(OTP_ID_PREFIX, applied.lastFilledIndex);
      }
    },
    [getValues, setValue],
  );

  const submitLead = useCallback(
    async (values: ReferralFormValues, verifyResponse: any) => {
      setIsSubmitUIBusy(true);

      const ipJsonData = getIpJSONData() || {};
      const body = {
        leadName: values.fullName,
        leadEmailAddress: values.email,
        countryCode: values.countryCode,
        leadPhone: values.mobile,
        programID: values.program.ProgramID,
        programName: values.program.ProgramName,
        specialization: values.program.Specialization,
        pageUrl: window.location.href,
        referrerEmail: values.verificationEmailOrMobile,
        utm_source: "Student Referral for Amity Programs",
        ReferrerAmityEmail: verifyResponse?.AmityEmail,
        ReferrerEnrollmentNo: verifyResponse?.EnrollmentNo,
        ReferrerName: verifyResponse?.Name,
        ReferrerPersonalEmail: verifyResponse?.PersonalEmail,
        ReferrerPhone: verifyResponse?.Phone,
        ReferrerProgramName: verifyResponse?.ProgramName,
        AMSID: verifyResponse?.AMSID,
        SanctionAmount: `${values.counsellorUsername}@univoedtech.com` || "",
        ...ipJsonData,
      };

      const tracking = {
        ...ipJsonData,
        body,
      };

      const form = {
        PROGRAM_ID: String(values.program.ProgramID),
        consent: true,
        countryCode: values.countryCode,
        degree: values.program.ProgramName,
        email: values.email,
        name: values.fullName,
        phone: values.mobile,
        program: values.program.ProgramName,
      };

      try {
        const result = await submitReferralLead(body);
        trackReferralCounsellor(true, form, ipStackDetails, tracking);

        if (result.response?.body?.status === "Lead already exists") {
          setError("otp", {
            type: "server",
            message: "Lead already exists.",
          });
        } else {
          setStep(2);
        }
      } catch (error) {
        console.error("Error submitting the form:", error);
        trackReferralCounsellor(false, form, ipStackDetails, tracking);
      } finally {
        setIsSubmitUIBusy(false);
      }
    },
    [ipStackDetails, newData, setError],
  );

  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e?.preventDefault?.();
      if (isSubmitting || isSubmitDisabled || verificationServerError) return;

      scheduleAfterPaint(() => {
        void (async () => {
          try {
            const values = getValues();
            const valid = await trigger(OTP_GATE_FIELDS);
            if (!valid) return;
            if (!showOtpBox) return;

            if (!(values.otp ?? []).every(Boolean)) {
              setError("otp", {
                type: "validate",
                message: "Please enter the OTP.",
              });
              return;
            }

            setIsSubmitUIBusy(true);

            const verify = await verifyReferralFormOtp({
              mobile: values.verificationEmailOrMobile,
              otp: values.otp.join(""),
            });

            if (verify.expired) {
              setError("otp", {
                type: "validate",
                message: "OTP has expired. Please resend OTP.",
              });
              setIsSubmitUIBusy(false);
              return;
            }
            if (!verify.success) {
              setError("otp", {
                type: "server",
                message: "Incorrect OTP. Please try again.",
              });
              setIsSubmitUIBusy(false);
              return;
            }
            const veerifyResponse = verify?.response || {}

            await submitLead(values, veerifyResponse);
          } catch {
            setIsSubmitUIBusy(false);
          }
        })();
      });
    },
    [
      getValues,
      isSubmitDisabled,
      isSubmitting,
      setError,
      showOtpBox,
      submitLead,
      trigger,
      verificationServerError,
    ],
  );

  const handleReferMore = useCallback(() => {
    if (!enableReferMoreReset) {
      window.location.href = "/referral";
      return;
    }
    setValue("fullName", "");
    setValue("email", "");
    setValue("countryCode", countryCode || NATIONAL_CODE);
    setValue("mobile", "");
    setValue("program", { ...EMPTY_PROGRAM });
    setValue("counsellorUsername", "");
    setValue("verificationEmailOrMobile", "");
    setValue("otp", createEmptyOtp());
    clearErrors();
    setVerificationServerError(undefined);
    setShowOtpBox(false);
    setIsSubmitUIBusy(false);
    setIsValidationUsed(false);
    setTimer(0);
    setResendEnabled(false);
    isFiredOnce.current = false;
    setStep(1);
  }, [clearErrors, countryCode, enableReferMoreReset, setValue]);

  return {
    step,
    control,
    programs,
    errors,
    showOtpBox,
    isSubmitting,
    isSubmitDisabled,
    isValidationDisabled,
    isValidationUsed,
    timer,
    resendEnabled,
    timerLabel: formatOtpTimer(timer),
    stickyCtaRef: enableStickyCta ? stickyRef : null,
    handleChange,
    handleProgramChange,
    handleCountryCodeChange,
    updateCountryCode,
    fetchOTP,
    resendOTP,
    handleOtpChange,
    handleOtpKeyDown,
    handlePaste,
    otpRef,
    handleFormSubmit,
    handleReferMore,
  };
}
